/**
 * Installations Query Hooks
 * React Query hooks for installations data fetching and mutations
 * Replaces InstallationsContext with React Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { installationsService } from 'src/api/services';
import { useToast } from 'src/Contexts/ToastContext';
import { queryKeys } from './queryKeys';
import { useProjectsQuery } from './useProjectsQuery';
import type { Installation } from 'src/types';

/**
 * Hook to fetch installations for the current user's projects
 * This is a dependent query that waits for projects to load first
 *
 * @returns React Query result with installations data
 */
export function useInstallationsQuery() {
  const { data: projects = [], isLoading: projectsLoading } = useProjectsQuery();

  // Extract installation IDs from all projects
  const installationIds = projects.flatMap((project) => project.installation_ids || []);

  return useQuery({
    queryKey: queryKeys.installations.byIds(installationIds),
    queryFn: async () => {
      if (installationIds.length === 0) return [];
      return await installationsService.getByIds(installationIds);
    },
    enabled: !projectsLoading && installationIds.length > 0, // Wait for projects first
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all installations
 * Used in management/admin interfaces
 *
 * @returns React Query result with all installations
 */
export function useAllInstallationsQuery() {
  return useQuery({
    queryKey: queryKeys.installations.lists(),
    queryFn: () => installationsService.getAll(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to fetch a single installation by ID
 *
 * @param id - Installation ID
 * @returns React Query result with installation data
 */
export function useInstallationQuery(id: string) {
  return useQuery({
    queryKey: queryKeys.installations.detail(id),
    queryFn: () => installationsService.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Mutation hook to create a new installation
 * Includes optimistic update and automatic cache invalidation
 *
 * @returns React Query mutation result
 */
export function useCreateInstallationMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (data: Partial<Installation>) => installationsService.create(data),

    // Optimistic update
    onMutate: async (newInstallation) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.installations.all });

      const previousInstallations = queryClient.getQueryData(queryKeys.installations.lists());

      queryClient.setQueryData<Installation[]>(queryKeys.installations.lists(), (old = []) => [
        ...old,
        { ...newInstallation, id: `temp-${Date.now()}` } as Installation,
      ]);

      return { previousInstallations };
    },

    onError: (err, _newInstallation, context) => {
      if (context?.previousInstallations) {
        queryClient.setQueryData(queryKeys.installations.lists(), context.previousInstallations);
      }
      toast.error('Failed to create installation');
      console.error('Create installation error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.installations.all });
      toast.success('Installation created successfully');
    },
  });
}

/**
 * Mutation hook to update an existing installation
 * Includes optimistic update and automatic cache invalidation
 *
 * @returns React Query mutation result
 */
export function useUpdateInstallationMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Installation> }) =>
      installationsService.update(id, data),

    // Optimistic update
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.installations.all });

      const previousInstallations = queryClient.getQueryData(queryKeys.installations.lists());

      // Update in list cache
      queryClient.setQueryData<Installation[]>(queryKeys.installations.lists(), (old = []) =>
        old.map((installation) =>
          installation.id === id ? { ...installation, ...data } : installation
        )
      );

      // Update in detail cache if exists
      queryClient.setQueryData<Installation>(queryKeys.installations.detail(id), (old) =>
        old ? { ...old, ...data } : old
      );

      return { previousInstallations };
    },

    onError: (err, { id: _id }, context) => {
      if (context?.previousInstallations) {
        queryClient.setQueryData(queryKeys.installations.lists(), context.previousInstallations);
      }
      toast.error('Failed to update installation');
      console.error('Update installation error:', err);
    },

    onSuccess: (_, { id: _id }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.installations.all });
      toast.success('Installation updated successfully');
    },
  });
}

/**
 * Mutation hook to delete an installation
 * Includes optimistic update and automatic cache invalidation
 *
 * @returns React Query mutation result
 */
export function useDeleteInstallationMutation() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => installationsService.delete(id),

    // Optimistic update
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.installations.all });

      const previousInstallations = queryClient.getQueryData(queryKeys.installations.lists());

      queryClient.setQueryData<Installation[]>(queryKeys.installations.lists(), (old = []) =>
        old.filter((installation) => installation.id !== id)
      );

      return { previousInstallations };
    },

    onError: (err, _id, context) => {
      if (context?.previousInstallations) {
        queryClient.setQueryData(queryKeys.installations.lists(), context.previousInstallations);
      }
      toast.error('Failed to delete installation');
      console.error('Delete installation error:', err);
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.installations.all });
      toast.success('Installation deleted successfully');
    },
  });
}
